import type { ODataResponse } from '../../models/common';
import type { Task } from '../../models/task';
import type { TaskIntegration } from '../../models/taskIntegration';
import type { FetchResponse } from '@/lib/api/api';
import api from '@/lib/api/api';

/**
 * Interfaccia per gli endpoint API dei Task
 */
export interface ITaskEndpoints {
	/** Recupera tutti i task con query opzionale */
	getTasks(query: string): Promise<FetchResponse<ODataResponse<Task>>>;
	/** Recupera un task specifico per ID */
	getTask(id: number): Promise<FetchResponse<Task>>;
	/** Aggiorna un task esistente */
	updateTask(id: number, payload: Partial<Task>): Promise<FetchResponse<Task>>;
	/** Crea un nuovo task */
	postTask(payload: Partial<Task>): Promise<FetchResponse<Task>>;
	/** Elimina un task */
	deleteTask(id: number): Promise<FetchResponse<void>>;
	/** Recupera le integrazioni per un task */
	getIntegrations(taskId: number): Promise<FetchResponse<ODataResponse<TaskIntegration>>>;
}

/**
 * Implementazione degli endpoint API per i Task
 * Gestisce le operazioni CRUD sui task e le relative integrazioni
 */
const taskEndpoints: ITaskEndpoints = {
	/**
	 * Recupera tutti i task con filtri OData
	 * @param query - Query string con parametri OData ($filter, $orderby, etc.)
	 * @returns Promise con la risposta OData contenente i task
	 */
	getTasks(query: string): Promise<FetchResponse<ODataResponse<Task>>> {
		return api.get(`/odata/v1/Tasks${query}`);
	},

	/**
	 * Recupera un task specifico per ID
	 * @param id - ID del task da recuperare
	 * @returns Promise con il task trovato
	 */
	getTask(id: number): Promise<FetchResponse<Task>> {
		return api.get(`/odata/v1/Tasks/${id}`);
	},

	/**
	 * Aggiorna un task esistente
	 * @param id - ID del task da aggiornare
	 * @param payload - Dati parziali del task da aggiornare
	 * @returns Promise con il task aggiornato
	 */
	updateTask(id: number, payload: Partial<Task>): Promise<FetchResponse<Task>> {
		return api.put(`/api/v1/Tasks/${id}`, payload);
	},

	/**
	 * Crea un nuovo task
	 * @param payload - Dati del task da creare
	 * @returns Promise con il task creato
	 */
	postTask(payload: Partial<Task>): Promise<FetchResponse<Task>> {
		return api.post('/api/v1/Tasks', payload);
	},

	/**
	 * Elimina un task
	 * @param id - ID del task da eliminare
	 * @returns Promise void
	 */
	deleteTask(id: number): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/Tasks/${id}`);
	},

	/**
	 * Recupera le integrazioni per un task specifico
	 * @param taskId - ID del task di cui recuperare le integrazioni
	 * @returns Promise con la risposta OData contenente le integrazioni
	 */
	getIntegrations(taskId: number): Promise<FetchResponse<ODataResponse<TaskIntegration>>> {
		return api.get(`/odata/v1/TaskIntegrations?$filter=(sourceId eq ${taskId})`);
	},
};

export default taskEndpoints;
